import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import BasicFilter from '../../../global/components/table/helpers/BasicFilter'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import { ILocationFilters } from '../main/QueuesMain'
import WorkOrderDetailModal from '../../../work-orders/components/work-order-detail/components/main/WorkOrderDetailModal'
import { WoDetailContext } from '../../../work-orders/components/context/context'
import { IDeliveryCandidate } from '../../../../../app/entities/DeliveryCandidate'
import fetchDataAndSetTable from '../../../global/components/table/helpers/fetchDataAndSetTable'
import NoFilter from '../../../global/components/table/helpers/NoFilter'
import { DELIVERY_CANDIDATE_STATUS } from '../../../global/constants/delivery-candidate'
import RejectDeliveryCandidateModal from '../sub-modals/RejectDeliveryCandidateModal'
import { fetchData } from '../../../global/utils/fetch'
import { rejectDeliveryCandidate } from '../api'

interface ITireRackDeliveriesTableProps {
  locationFilters: ILocationFilters
}

function getStatusColor(status: DELIVERY_CANDIDATE_STATUS) {
  switch (status) {
    case DELIVERY_CANDIDATE_STATUS.AWAITING_SCHEDULING:
      return '#FFA500' // Orange
    case DELIVERY_CANDIDATE_STATUS.ORDER_CREATED:
      return '#008000' // Green
    case DELIVERY_CANDIDATE_STATUS.CANDIDATE_REJECTED:
      return '#FF0000' // Red
  }
}

const BASE_URL = `/queues/tire-rack-deliveries`

function TireRackDeliveriesTable(props: ITireRackDeliveriesTableProps) {
  const { locationFilters } = props
  const { queryOrderData, clearData } = useContext(WoDetailContext)
  const navigate = useNavigate()
  const [workOrderDetailsModalOpen, setWorkOrderDetailsModalOpen] =
    useState<boolean>(false)
  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false)
  const [selectedDeliveryCandidate, setSelectedDeliveryCandidate] =
    useState<IDeliveryCandidate | null>(null)
  const [tableState, setTableState] = useState({
    data: [] as IDeliveryCandidate[],
    isLoading: false,
    count: 0,
    pageSize: 100,
    offSet: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})

  async function handleWorkOrderDetailsModalOpen(
    deliveryCandidate: IDeliveryCandidate,
  ) {
    if (deliveryCandidate.status !== DELIVERY_CANDIDATE_STATUS.ORDER_CREATED)
      return
    setWorkOrderDetailsModalOpen(true)
    const url = `${BASE_URL}/${deliveryCandidate.objectId}`
    navigate(url)
    await queryOrderData(deliveryCandidate.objectId) //the for deliveries we make provide a custom orderObjectId which is the same as the deliveryCandidate.objectId
  }

  async function handleDetailsClosed() {
    setWorkOrderDetailsModalOpen(false)
    navigate(BASE_URL)
    clearData()
    refetchTable()
  }

  function handleRowClick(row: any) {
    const deliveryCandidate = row.original as IDeliveryCandidate
    if (
      deliveryCandidate.status === DELIVERY_CANDIDATE_STATUS.AWAITING_SCHEDULING
    ) {
      setSelectedDeliveryCandidate(deliveryCandidate)
      setRejectModalOpen(true)
    } else if (
      deliveryCandidate.status === DELIVERY_CANDIDATE_STATUS.ORDER_CREATED
    ) {
      handleWorkOrderDetailsModalOpen(deliveryCandidate)
    }
  }

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  async function handleRejectDeliveryCandidate() {
    if (!selectedDeliveryCandidate) return

    await fetchData<void>(
      rejectDeliveryCandidate(selectedDeliveryCandidate.objectId),
    )

    setRejectModalOpen(false)
    setSelectedDeliveryCandidate(null)
    refetchTable()
  }

  function handleCloseRejectModal() {
    setRejectModalOpen(false)
    setSelectedDeliveryCandidate(null)
  }

  const tireRackDeliveryQueueEndpoint = `${process.env.REACT_APP_ORIGIN}/delivery-candidates/queue`
  function refetchTable() {
    fetchDataAndSetTable<IDeliveryCandidate>({
      endpoint: tireRackDeliveryQueueEndpoint,
      queries: [
        { pageSize: tableState.pageSize.toString() },
        { offSet: tableState.offSet.toString() },
        { columnFilters: JSON.stringify(columnFilters) },
        { selectedLocationId: locationFilters.locationFilter },
      ],
      setTableState,
    })
  }

  useEffect(() => {
    refetchTable()
  }, [locationFilters, tableState.pageSize, tableState.offSet])

  const columns: Column<IDeliveryCandidate | any>[] = [
    //because tireRackDeliverData is a JSON object, we need to access its properties
    //with the columnFilters using the method that has been here implemented with the
    //static text additions 🙃
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row: { original } }) => {
        const { status } = original as IDeliveryCandidate
        return (
          <div
            style={{
              padding: '4px 8px',
              borderRadius: '5px',
              border: `1px solid ${getStatusColor(status)}`,
              color: getStatusColor(status),
              fontSize: '12px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
          </div>
        )
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            status: val,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'TR Order ID',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.tireRackOrderNumber,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"tireRackOrderNumber": "%${val}%"`,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Delivery Date',
      accessor: (row: IDeliveryCandidate) => {
        const deliveryDate = new Date(row.deliveryDate)
        const [year, month, day] = deliveryDate
          .toISOString()
          .split('T')[0]
          .split('-')
        return `${month}/${day}/${year.slice(2, 4)}`
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            deliveryDate: val,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Location Name',
      accessor: (row: IDeliveryCandidate) => row.location.name,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            location: { name: val },
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Customer Full Name',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.customerFullName,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"customerFullName": "%${val.toUpperCase()}%"`,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Delivery Address',
      accessor: (row: IDeliveryCandidate) => {
        const { tireRackDeliveryData: data } = row
        return `${data.installationPointAddress}, ${data.installationPointCity}, ${data.installationPointState} ${data.installationPointZipCode}`
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"installationPointAddress": "%${val.toUpperCase()}%"`,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Customer Phone',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.customerPhoneNumber,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"customerPhoneNumber": "%${val}%"`,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Customer Email',
      accessor: (row: IDeliveryCandidate) =>
        row.tireRackDeliveryData.customerEmail,
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"customerEmail": "%${val}%"`,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Customer Vehicle',
      accessor: (row: IDeliveryCandidate) => {
        const { tireRackDeliveryData: data } = row
        return `${data.vehicleYearAbbreviated} ${
          data.vehicleBrandAbbreviated
        } ${data.vehicleModelName} ${data.vehicleModelVariant || ''}`.trim()
      },
      Filter: NoFilter(),
    },
    {
      Header: 'Tire Qty',
      accessor: 'tires',
      Cell: ({ row: { original } }) => {
        const { tireRackDeliveryData } = original as IDeliveryCandidate
        const tiresQuantity = tireRackDeliveryData.tireQuantity
        return <>{tiresQuantity}</>
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"tireQuantity": "%${val}%"`,
          }),
        onEnter: refetchTable,
      }),
    },
    {
      Header: 'Tire Description',
      accessor: (row: IDeliveryCandidate) => {
        const { tireRackDeliveryData: data } = row
        return `${data.tireManufacturer} - ${data.tireDescription}`
      },
      Filter: NoFilter(),
    },
    {
      Header: 'Freight Cost',
      accessor: 'freightCost',
      Cell: ({ row: { original } }) => {
        const { tireRackDeliveryData } = original as IDeliveryCandidate
        const freightCost = tireRackDeliveryData.freightTotal
        return <>${freightCost}</>
      },
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({
            tireRackDeliveryData: `"freightTotal": "%${val}%"`,
          }),
        onEnter: refetchTable,
      }),
    },
  ]
  return (
    <div style={{ width: '100%' }}>
      <WorkOrderDetailModal
        handleClosed={handleDetailsClosed}
        isOpen={workOrderDetailsModalOpen}
      />
      <RejectDeliveryCandidateModal
        isOpen={rejectModalOpen}
        deliveryCandidate={selectedDeliveryCandidate}
        handleClose={handleCloseRejectModal}
        handleConfirm={handleRejectDeliveryCandidate}
      />
      <div
        style={{
          marginBottom: '10px',
          fontSize: '14px',
          color: '#555',
          fontStyle: 'italic',
        }}
      >
        Only Tire Rack Deliveries currently eligible, or already scheduled, for
        delivery on the next available day are displayed here. Please go to the
        Work Orders page for all scheduled deliveries.
      </div>
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={handleRowClick}
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default TireRackDeliveriesTable
